import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Show,
    useUpdate,
    useDelete,
    useCanAccess,
    useDataProvider,
    useGetIdentity,
    useNotify,
    useRecordContext,
    useRefresh,
} from 'react-admin';
import type { CrmDataProvider } from '@/providers/types';
import {
    Box,
    Stack,
    Typography,
    TextField,
    Select,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    Paper,
    Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { CardJobs } from './CardJobs';
import { CardProjectFields } from './CardProjectFields';
import { format, parseISO } from 'date-fns';
import type { Card, CardProjectOption, CardStatus } from '@/types';
import {
    CARD_STATUSES,
    formatLimitDate,
    isOverdue,
    toDateTimeLocalInput,
    DONE,
} from './cardStatus';
import { parseError } from './cardApi';
import { CardAttachmentList } from './CardAttachmentList';
import { CardComments } from './CardComments';

interface CardShowContentProps {
    onClose: () => void;
}

const CardShowContent = ({ onClose }: CardShowContentProps) => {
    const record = useRecordContext<Card>();
    const refresh = useRefresh();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [update] = useUpdate();
    const [deleteRecord] = useDelete();
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState<{
        title?: string;
        description?: string | null;
        limit_date?: string | null;
        assigned_to?: number;
        project?: CardProjectOption | null;
        field_step_id?: number | null;
    }>({});
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [newStatus, setNewStatus] = useState<CardStatus>(record?.status ?? 0);
    // Keep the status select in sync with the record — if the user moved the card on the
    // board (which updates it via a raw fetch, outside react-admin's cache), the record may
    // arrive here with a different status than our initial local state.
    useEffect(() => {
        if (record) setNewStatus(record.status);
    }, [record?.status]);

    const { canAccess: canUpdate } = useCanAccess({
        resource: 'cards',
        action: 'edit',
    });

    const { canAccess: canDelete } = useCanAccess({
        resource: 'cards',
        action: 'delete',
    });

    const { identity } = useGetIdentity();
    const isAdmin = Boolean(
        (identity as { administrator?: boolean } | undefined)?.administrator
    );
    const currentUserId = identity?.id as number | undefined;

    if (!record) return null;

    const isCreator = currentUserId != null && record.created_by === currentUserId;
    const isAssignee = currentUserId != null && record.assigned_to === currentUserId;
    const canEditFields = !!canUpdate && (isAdmin || isCreator);
    const canChangeStatus = !!canUpdate && (isAdmin || isCreator || isAssignee);

    const handleEditStart = () => {
        setEditData({
            title: record.title,
            description: record.description,
            limit_date: toDateTimeLocalInput(record.limit_date),
            assigned_to: record.assigned_to,
            project: record.project_id
                ? { id: record.project_id, name: record.project_name ?? '' }
                : null,
            field_step_id: record.field_step_id ?? null,
        });
        setIsEditing(true);
    };

    const handleEditSave = async () => {
        // The API takes project_id / field_step_id, not the project object held in editData.
        const { project, field_step_id, limit_date, ...fields } = editData;
        try {
            await update(
                'cards',
                {
                    id: record.id,
                    data: {
                        ...fields,
                        // datetime-local gives local wall time; send it as UTC ISO.
                        limit_date: limit_date ? new Date(limit_date).toISOString() : null,
                        project_id: project?.id ?? null,
                        field_step_id: field_step_id ?? null,
                    },
                },
                { returnPromise: true }
            );
            setIsEditing(false);
            refresh();
            notify('Card updated', { type: 'success' });
        } catch (err) {
            notify(parseError((err as { body?: unknown })?.body, 'Failed to update card'), {
                type: 'error',
            });
        }
    };

    const handleDelete = async () => {
        try {
            await deleteRecord('cards', { id: record.id });
            setShowDeleteDialog(false);
            onClose();
        } catch (err) {
            console.error('Failed to delete card', err);
        }
    };

    const overdue = isOverdue(record.limit_date, record.status);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header with title and status */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        {record.title}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {canChangeStatus && (
                        <Select
                            value={newStatus}
                            onChange={async e => {
                                const status = e.target.value as CardStatus;
                                const previous = newStatus;
                                setNewStatus(status);
                                try {
                                    await dataProvider.updateCardStatus(record.id, status);
                                    refresh();
                                    notify('Status updated', { type: 'success' });
                                } catch (err) {
                                    setNewStatus(previous);
                                    notify(
                                        err instanceof Error
                                            ? err.message
                                            : 'Failed to update status',
                                        { type: 'error' }
                                    );
                                }
                            }}
                            sx={{ minWidth: 120 }}
                            size="small"
                        >
                            {CARD_STATUSES.map(s => (
                                <MenuItem key={s.id} value={s.id}>
                                    {s.label}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                    {canEditFields && !isEditing && (
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={handleEditStart}
                            size="small"
                        >
                            Edit
                        </Button>
                    )}
                    {canDelete && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setShowDeleteDialog(true)}
                            size="small"
                        >
                            Delete
                        </Button>
                    )}
                </Stack>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Details section */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'action.hover' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={3}
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Assigned to
                        </Typography>
                        <Typography variant="body2">{record.assigned_to_name}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Created by
                        </Typography>
                        <Typography variant="body2">{record.created_by_name}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                            Created at
                        </Typography>
                        <Typography variant="body2">
                            {format(parseISO(record.created_at), 'PPp')}
                        </Typography>
                    </Box>
                    {record.limit_date && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Date
                            </Typography>
                            <Chip
                                label={formatLimitDate(record.limit_date)}
                                color={overdue ? 'error' : 'default'}
                                variant={overdue ? 'filled' : 'outlined'}
                                size="small"
                            />
                        </Box>
                    )}
                    {record.project_name && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Project
                            </Typography>
                            <Typography variant="body2">{record.project_name}</Typography>
                        </Box>
                    )}
                    {record.field_step_name && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Field step
                            </Typography>
                            <Typography variant="body2">{record.field_step_name}</Typography>
                        </Box>
                    )}
                </Stack>
            </Paper>

            {/* Description section */}
            {isEditing && canEditFields ? (
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <TextField
                        label="Title"
                        fullWidth
                        value={editData.title || ''}
                        onChange={e => setEditData({ ...editData, title: e.target.value })}
                    />
                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={4}
                        value={editData.description || ''}
                        onChange={e => setEditData({ ...editData, description: e.target.value })}
                    />
                    <TextField
                        type="datetime-local"
                        label="Date"
                        InputLabelProps={{ shrink: true }}
                        value={editData.limit_date || ''}
                        onChange={e => setEditData({ ...editData, limit_date: e.target.value })}
                    />
                    <CardProjectFields
                        project={editData.project ?? null}
                        fieldStepId={editData.field_step_id ?? null}
                        onChange={(project, field_step_id) =>
                            setEditData(prev => ({ ...prev, project, field_step_id }))
                        }
                    />
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={handleEditSave}>
                            Save
                        </Button>
                        <Button variant="outlined" onClick={() => setIsEditing(false)}>
                            Cancel
                        </Button>
                    </Stack>
                </Stack>
            ) : (
                <>
                    {record.description && (
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    backgroundColor: 'background.paper',
                                    p: 2,
                                    borderRadius: 1,
                                }}
                            >
                                {record.description}
                            </Typography>
                        </Box>
                    )}

                </>
            )}

            {/* Attachments section — shown when there are files or the user can upload. */}
            {(canEditFields || (record.attachments && record.attachments.length > 0)) && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                        Attachments ({record.attachment_count})
                    </Typography>
                    <CardAttachmentList
                        cardId={record.id}
                        attachments={record.attachments || []}
                        canRemove={canEditFields}
                        canUpload={canEditFields}
                        onChanged={() => refresh()}
                    />
                </Box>
            )}

            {/* Comments section */}
            <Divider sx={{ my: 3 }} />
            <CardComments card={record} canUpdate={!!canUpdate} onChanged={() => refresh()} />

            {/* Delete confirmation dialog */}
            <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
                <DialogTitle>Delete card</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this card? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export const CardShow = () => {
    const navigate = useNavigate();
    const handleClose = () => navigate('/cards', { replace: true });

    return (
        <>
            <CardJobs />
            <Dialog
                open
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                aria-labelledby="card-show-title"
            >
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Show
                        resource="cards"
                        component="div"
                        queryOptions={{
                            meta: { includes: 'card_attachments,card_comments' },
                        }}
                    >
                        <CardShowContent onClose={handleClose} />
                    </Show>
                </Box>
            </Dialog>
        </>
    );
};

CardShow.displayName = 'CardShow';
